import { useParams } from 'react-router-dom';
import { useState, type ReactNode, type RefObject } from 'react';
import { Icon } from '@/lib/icons';
import { useConfirm } from '@/hooks/use-confirm';
import { TaskAudioPlayer, TaskItemCard, TaskItemField, TaskItemText } from '@/features/contribution-kit';
import type { NormalizedAudio } from './types';
import { AudioTextCorrectionForm } from './AudioTextCorrectionForm';
import type { AudioTextCorrectionCopy, AudioTextCorrectionRequest } from './audio-text-correction.contract';

/** Present only for roles allowed to correct a text (spec 003). */
type TextCorrectionControl = {
  copy: AudioTextCorrectionCopy;
  isSaving: boolean;
  /** Resolves once saved; rejects to keep the form open. */
  onSave: (request: AudioTextCorrectionRequest) => Promise<void>;
};

type ValidateAudioItemCardProps = {
  t: Record<string, unknown>;
  current: NormalizedAudio;
  isPlaying: boolean;
  audioRef: RefObject<HTMLAudioElement>;
  canvasRef: RefObject<HTMLCanvasElement>;
  onPlayAudio: () => void;
  onAudioEnded: () => void;
  hasValidated: boolean;
  hasRejected: boolean;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDeleteAudio?: () => void;
  textCorrection?: TextCorrectionControl;
  /** Detected language row, between the player and the expected text. */
  languageRow: ReactNode;
  footer?: ReactNode;
};

/**
 * The audio to judge: the phrase it should say (or the question it answers),
 * the player, the expected French, the moderator's tools (delete, text
 * correction) and the votes already cast.
 */
export function ValidateAudioItemCard({
  t,
  current,
  isPlaying,
  audioRef,
  canvasRef,
  onPlayAudio,
  onAudioEnded,
  hasValidated,
  hasRejected,
  canDelete,
  isDeleting,
  onDeleteAudio,
  textCorrection,
  languageRow,
  footer,
}: ValidateAudioItemCardProps) {
  const s = (key: string, fallback = '') => String(t[key] ?? fallback);
  const { lang } = useParams<{ lang: string }>();
  const frenchMissing = lang === 'en' ? 'Not provided' : 'Non renseigné';
  const confirm = useConfirm();
  // The recorded answer to an initial-data question is a free answer in the
  // local language, not the reading of a French sentence.
  const isInitialAnswer = current.source === 'initialAudio';
  // Keyed by audio id, so moving to another audio closes the editor.
  const [editingAudioId, setEditingAudioId] = useState<string | null>(null);
  const isEditingText = editingAudioId === current.id;
  const saveText = (request: AudioTextCorrectionRequest) => {
    textCorrection?.onSave(request).then(() => setEditingAudioId(null), () => undefined);
  };
  const askDelete = async () => {
    const ok = await confirm({
      title: s('deleteAudioTitle'),
      description: s('deleteAudioDescription'),
      confirmLabel: s('delete'),
      cancelLabel: s('cancel'),
      tone: 'danger',
    });
    if (ok) onDeleteAudio?.();
  };

  return (
    <TaskItemCard
      label={isInitialAnswer ? s('audioAnswerToQuestion') : s('currentWord')}
      corner={
        canDelete ? (
          <button
            type="button"
            onClick={askDelete}
            disabled={isDeleting}
            aria-label={s('deleteAudioButton')}
            className="inline-flex size-11 items-center justify-center rounded-full border border-danger/25 text-danger transition-colors hover:bg-danger/[0.08] disabled:opacity-40"
          >
            <Icon name="trash2" className="size-4" />
          </button>
        ) : null
      }
      footer={footer}
    >
      {isInitialAnswer ? (
        <div data-testid="validate-audio-answer-to-question" className="space-y-1">
          <TaskItemText quoted={false} className="py-2 text-lg sm:text-xl">
            {current.phraseFr?.trim() || frenchMissing}
          </TaskItemText>
          <p className="text-center text-xs leading-5 text-muted-foreground">{s('audioAnswerToQuestionHint')}</p>
        </div>
      ) : (
        <TaskItemText>{current.word}</TaskItemText>
      )}

      <TaskAudioPlayer
        isPlaying={isPlaying}
        onToggle={onPlayAudio}
        canvasRef={canvasRef}
        playLabel={s('playAudio')}
        pauseLabel={s('pauseAudio')}
      />
      <audio key={current.id} ref={audioRef} onEnded={onAudioEnded} className="hidden">
        <source src={current.audioUrl} type="audio/wav" />
      </audio>

      {languageRow}

      {/* What the contributor was asked to say, so a validator can judge the
          recording without knowing the local language. */}
      {!isInitialAnswer && (
        <TaskItemField label={s('audioExpectedFrench')} testId="validate-audio-expected-french">
          {current.phraseFr?.trim() ? current.phraseFr : frenchMissing}
        </TaskItemField>
      )}

      {textCorrection &&
        (isEditingText ? (
          <AudioTextCorrectionForm
            copy={textCorrection.copy}
            current={{ phraseLocal: current.phraseLocal ?? current.word, phraseFr: current.phraseFr ?? '' }}
            isInitialAnswer={isInitialAnswer}
            isSaving={textCorrection.isSaving}
            onSave={saveText}
            onCancel={() => setEditingAudioId(null)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingAudioId(current.id)}
            className="inline-flex h-11 items-center gap-2 text-sm font-medium text-brand hover:underline"
          >
            <Icon name="edit" className="size-4" />
            {textCorrection.copy.open}
          </button>
        ))}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Icon name="checkCircle" className="size-3.5 text-success" />
          {current.validatedBy.length} {s('validations')}
        </span>
        <span className="inline-flex items-center gap-1">
          <Icon name="x" className="size-3.5 text-danger" />
          {current.rejectedBy.length} {s('rejections')}
        </span>
        {current.recordingDurationMs != null && <span>{(current.recordingDurationMs / 1000).toFixed(2)}s</span>}
        {hasValidated && <span className="font-medium text-success">{s('validatedByYou')}</span>}
        {hasRejected && <span className="font-medium text-danger">{s('rejectedByYou')}</span>}
      </div>
    </TaskItemCard>
  );
}
